/* global window console document */

import { isTrustedSnapshotOrigin } from "./allowedOrigins.js";

// Seam 2 of the deck snapshot protocol (§6.6). A host frame (SlideCraft) that
// is about to print a deck can't keep live cross-origin Gridd iframes in the
// printed page, so it asks each embed for a static raster of its *current*
// render and swaps in an <img>. This module answers that request from inside
// the iframe.
//
//   Host   -> iframe: { type: "gridd-snapshot-request",  id, format, scale }
//   iframe -> Host:   { type: "gridd-snapshot-response", id, format, dataUrl, width, height }
//                 or  { type: "gridd-snapshot-error",    id, message }
//
// We rasterize the already-rendered grid DOM with html2canvas — the same
// rasterizer the PowerPoint bake path (ppt/basicBake.js) already uses — so web
// fonts and same-origin cell images are inlined into the canvas and the emitted
// PNG data: URL is fully self-contained (no external resources at print time).

export const SNAPSHOT_REQUEST_TYPE = "gridd-snapshot-request";
export const SNAPSHOT_RESPONSE_TYPE = "gridd-snapshot-response";
export const SNAPSHOT_ERROR_TYPE = "gridd-snapshot-error";

// The grid cells (text + backgrounds). Used to anchor the capture and as the
// fallback root when the full grid container isn't present.
const GRID_ROOT_ID = "gridd_content";

// The cells+headers wrapper. Its parent is the grid's inner container, which is
// the nearest common ancestor of the cells (#gridd_content) AND the border
// overlay (#border_grid, an absolutely-positioned SIBLING of this wrapper).
// Capturing that ancestor is what keeps borders — and the grid's own background
// and outer edges — in the raster; capturing #gridd_content alone drops them.
const GRID_WRAPPER_ID = "gridd_with_headers";

// DPR multiplier. The host asks for 2 so a 1600x900 page prints crisp; the
// response reports the *logical* box so the <img> lays out unchanged (sharp,
// same pagination). Clamped so a stray/huge value can't blow up canvas memory.
const DEFAULT_SCALE = 2;
const MAX_SCALE = 4;

// Handle a gridd-snapshot-request. Fire-and-forget from the caller's side: this
// posts back its own gridd-snapshot-response / gridd-snapshot-error and never
// throws. `event` is the original MessageEvent, so we reply to the exact frame
// that asked, at its browser-supplied (unforgeable) origin.
export async function handleSnapshotRequest(data, event) {
  const { id, scale } = data || {};
  const target = event && event.source ? event.source : window.parent;
  const targetOrigin = event && event.origin ? event.origin : "*";

  // A snapshot exposes the grid's rendered pixels to the parent — data the
  // same-origin policy would otherwise withhold from a cross-origin embedder.
  // Refuse (silently, no response at all) any request whose real handshake
  // origin is not a trusted host. Fail-closed: an unknown origin gets nothing.
  if (!isTrustedSnapshotOrigin(event && event.origin)) {
    console.warn("gridd snapshot refused: untrusted origin", event && event.origin);
    return;
  }

  const respondError = (message) => {
    console.warn("gridd snapshot failed:", message);
    if (target) {
      target.postMessage({ type: SNAPSHOT_ERROR_TYPE, id, message: String(message) }, targetOrigin);
    }
  };

  try {
    const content = document.getElementById(GRID_ROOT_ID);
    if (!content) {
      respondError(`snapshot target #${GRID_ROOT_ID} not found`);
      return;
    }
    // Prefer the grid's inner container (the wrapper's parent) so the border
    // overlay is included; fall back to the cells-only node if it's absent.
    const wrapper = document.getElementById(GRID_WRAPPER_ID);
    const node = (wrapper && wrapper.parentElement) || content;

    const effectiveScale = Number.isFinite(scale) && scale > 0 ? Math.min(scale, MAX_SCALE) : DEFAULT_SCALE;

    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(node, {
      scale: effectiveScale,
      backgroundColor: null, // preserve transparency; the grid cells paint themselves
      useCORS: true,
      logging: false,
    });

    const dataUrl = canvas.toDataURL("image/png");

    // Report the logical (pre-scale) box so the host lays the <img> out at the
    // same size the iframe occupied — the data: URL just carries more pixels.
    target.postMessage(
      {
        type: SNAPSHOT_RESPONSE_TYPE,
        id,
        // PNG is the v1 format. A "svg" request still gets a PNG for now (the
        // internal rasterizer is canvas-based); we echo what we actually made.
        format: "png",
        dataUrl,
        width: Math.round(canvas.width / effectiveScale),
        height: Math.round(canvas.height / effectiveScale),
      },
      targetOrigin
    );
  } catch (e) {
    respondError(e && e.message ? e.message : e);
  }
}
